import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, EButtonType, Input, SwitchSize, SwitchType } from '@repo/apg-ui';
import { ReactComponent as BuyIcon } from '../../assets/icons/new/basket2.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit_outline.svg';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { getPathname } from '../../utils/paths';
import './BottomNavigation.scss';
import { useNavigate } from 'react-router';
import { useMedia } from 'react-use';
import classNames from 'classnames';
import Tooltip from 'rc-tooltip';

interface ITwoButtons {
  prevCallback?: () => void;
  nextCallback?: () => void;
  isDisabled?: boolean;
  isResultsDisabled?: boolean;
  isLoading?: boolean;
  isAgencyCheckout?: boolean;
  isHasResultsJump?: boolean;
  total?: number;
  isSkipNextStep?: boolean;
  isReservation?: boolean;
  agencyInfoPanelVisibility?: boolean;
  changeAgencyInfoPanelVisibility?: (value: boolean) => void;
  isAgencyJourney?: boolean;
  agencyData?: any;
}

const BottomNavigation = ({
  prevCallback,
  nextCallback,
  isDisabled,
  isResultsDisabled,
  isLoading,
  isHasResultsJump,
  isAgencyJourney,
  agencyInfoPanelVisibility,
  changeAgencyInfoPanelVisibility,
  agencyData,
  total,
  isAgencyCheckout,
  isSkipNextStep,
  isReservation,
}: ITwoButtons) => {
  const intl = useIntl();
  const params = useParams();
  const step = parseInt(params?.step);
  const containers = useSelector((state: RootState) => state.app.containers);
  const OPEN_BASKET_STEP = 4;
  const FINAL_STEP = 5;
  const isMobile = useMedia('(max-width: 768px)');
  const navigate = useNavigate();

  const getPrevPath = () => {
    switch (step) {
      default:
        return step - 1;
    }
  };

  const clickPrev = () => {
    prevCallback && prevCallback();

    if (isAgencyCheckout) {
      return;
    } else {
      navigate(getPathname(containers)(getPrevPath()));
    }
  };

  const getNextPath = () => {
    switch (step) {
      default:
        return step + 1;
    }
  };

  const clickNext = () => {
    if (step === FINAL_STEP || step === OPEN_BASKET_STEP) {
      nextCallback && nextCallback();
      return;
    }
    nextCallback && nextCallback();
    if (isSkipNextStep) {
      return;
    }
    navigate(getPathname(containers)(getNextPath()));
  };

  const jumpToResults = () => {
    navigate(getPathname(containers)(4));
    nextCallback && nextCallback();
  };

  const getPrevLabel = () => {
    if (isMobile) {
      return <FormattedMessage id='ec.components.buttons.back' defaultMessage='Zurück' />;
    }

    switch (step) {
      case 5:
        return <FormattedMessage id='ec.components.buttons.backToShop' defaultMessage='Zurück zum Shop' />;
      default:
        return <FormattedMessage id='ec.components.buttons.back' defaultMessage='Zurück' />;
    }
  };

  const getNextLabel = () => {
    if (isReservation) {
      return (
        <FormattedMessage
          id='ec.components.buttons.bookForTotalAgency'
          defaultMessage='Offerte erstellen'
          values={{ total: total }}
        />
      );
    }

    if (isAgencyCheckout) {
      return (
        <FormattedMessage id='ec.components.buttons.bookForTotal' defaultMessage='Buchen' values={{ total: total }} />
      );
    }

    switch (step) {
      case 1:
        if (isResultsDisabled) {
          return (
            <FormattedMessage id='ec.components.buttons.selectAreaFirst' defaultMessage='Zuerst ein Gebiet wählen' />
          );
        } else {
          return <FormattedMessage id='ec.components.buttons.next' defaultMessage='Weiter' />;
        }
      case 4:
        return (
          <div className='d-flex align-items-center'>
            <BuyIcon />
            <FormattedMessage id='ec.components.buttons.addToCard' defaultMessage='Zum Warenkorb' />
          </div>
        );
      case 5:
        return (
          <FormattedMessage id='ec.components.buttons.bookForTotal' defaultMessage='Buchen' values={{ total: total }} />
        );
      case 8:
        return <FormattedMessage id='ec.components.buttons.bookNow' defaultMessage='Jetzt buchen' />;
      default:
        return <FormattedMessage id='ec.components.buttons.next' defaultMessage='Weiter' />;
    }
  };

  const showResultsJump = isHasResultsJump && !isAgencyJourney;

  return (
    <div
      className={classNames('d-flex bottomNavigation w-100 flex-column align-items-center', {
        isAgencyMode: isAgencyJourney,
        'justify-content-start': agencyInfoPanelVisibility,
        'justify-content-center': !agencyInfoPanelVisibility,
      })}
    >
      {isAgencyJourney && agencyInfoPanelVisibility && (
        <div className={'w-100 bg-red text-white d-flex justify-content-center agency-journey-panel'}>
          <div className={'innerContent d-flex justify-content-between'}>
            <div className='d-flex align-items-center pe-24'>
              <Tooltip
                overlayClassName={'rc-tooltip-bg-white'}
                placement={'top'}
                overlay={<FormattedMessage id='ec.agency.infoText' defaultMessage='Neue Kampagne erfassen' />}
              >
                <InfoIcon className='me-3 info-icon' />
              </Tooltip>
              <div className='d-flex flex-column'>
                <div className='font-14'>
                  <FormattedMessage id='ec.agency.agencyMode' defaultMessage='Agentur-Modus' />
                </div>
                <Button
                  buttonType={EButtonType.BasicLink}
                  className='text-white mt-0 text-underline text-start'
                  onClick={() => {
                    navigate('/agency');
                  }}
                >
                  <FormattedMessage id='ec.agency.toAgencyPanel' defaultMessage='Neue Kampagne erfassen' />
                </Button>
              </div>
            </div>
            <div className='d-flex gap-5'>
              <div className='d-flex flex-column'>
                <div className='font-10'>
                  <FormattedMessage id='ec.agency.campaignName' defaultMessage='Kampagnenname' />
                </div>
                <div className='font-14 font-bold'>{agencyData?.campaignName}</div>
              </div>
              <div className='d-flex flex-column'>
                <div className='font-10'>
                  <FormattedMessage id='ec.agency.clientName' defaultMessage='Kundenname' />
                </div>
                <div className='font-14 font-bold'>
                  <span>{agencyData?.customer?.display || agencyData?.customer?.agenturname}</span>
                  <Button
                    buttonType={EButtonType.BasicLink}
                    className='text-white ms-3 mt-0 text-underline'
                    onClick={() => {
                      navigate({ search: '?modalContent=editCampaign' });
                    }}
                  >
                    <EditIcon className='me-2 edit-icon' />
                    <FormattedMessage id='ec.agency.edit' defaultMessage='Edit' />
                  </Button>
                </div>
                <div className='font-14 font-bold'></div>
              </div>
              <div className='d-flex flex-column'>
                <div className='font-10'>
                  <FormattedMessage id='ec.agency.clientIndustry' defaultMessage='Branche' />
                </div>
                <div className='font-14 font-bold'>
                  {agencyData?.customer?.branchelabel || agencyData?.customer?.branchenlabel}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={classNames('d-flex innerContent button-panel align-items-center w-100', {
          'justify-content-between': isAgencyJourney,
          'justify-content-end': !isAgencyJourney,
        })}
      >
        {isAgencyJourney && (
          <div className='d-flex justify-content-start align-items-center w-100'>
            <div className='d-flex align-items-center justify-content-start'>
              <Input
                name={'agencyPanelToggleInput'}
                type='switch'
                switchType={SwitchType.ROUNDED}
                size={SwitchSize.MIDDLE}
                inputWrapperClass={'m-0 me-3'}
                defaultChecked={agencyInfoPanelVisibility}
                value={agencyInfoPanelVisibility}
                onChange={(name, value) => changeAgencyInfoPanelVisibility(value)}
              />

              <div className='d-flex flex-column font-12 font-bold text-red lh-sm'>
                <span>
                  <FormattedMessage id='ec.agency.infoPanel.title' defaultMessage='Infopanel' />
                </span>
                <span>
                  {agencyInfoPanelVisibility ? (
                    <FormattedMessage id='ec.agency.infoPanel.show' defaultMessage='schliessen' />
                  ) : (
                    <FormattedMessage id='ec.agency.infoPanel.hide' defaultMessage='Öffnen' />
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
        <div className='d-flex justify-content-end align-items-center'>
          {step !== 1 && (
            <Button onClick={clickPrev} buttonType={EButtonType.InvertedPillLarge} className={'mt-0'}>
              {getPrevLabel()}
            </Button>
          )}
          <Button
            onClick={clickNext}
            buttonType={showResultsJump ? EButtonType.InvertedPillLarge : EButtonType.PillLarge}
            isDisabled={isDisabled}
            isLoading={isLoading}
            className={'mt-0'}
          >
            {isLoading ? '' : getNextLabel()}
          </Button>
          {showResultsJump && (
            <Button
              onClick={jumpToResults}
              buttonType={EButtonType.PillLarge}
              isDisabled={isResultsDisabled}
              isLoading={isLoading}
              className={'mt-0'}
            >
              {intl.formatMessage({
                id: 'ec.components.buttons.showSurfaces',
                defaultMessage: 'Flächen anzeigen',
              })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BottomNavigation;
